import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useT } from "../../../i18n";
import {
	type BuilderIntroStep,
	introStepPosition,
	markIntroSeen,
	nextIntroStep,
	suppressViewerHelpAutoOpen,
} from "./builderIntroState";
import "./BuilderIntro.css";

interface BuilderIntroProps {
	/** Controlled by the parent's first-visit gate; unmount closes the intro. */
	open: boolean;
	/** Chosen "start from a size preset" — parent opens the preset gallery. */
	onChooseTemplate: () => void;
	/** Chosen "set the dimensions myself" — parent keeps the seeded design. */
	onChooseBlank: () => void;
	/**
	 * Whether this sauna type actually has size presets to offer
	 * (`getTemplatesForType`). Barrel (`tunna`) has none, so offering the
	 * choice there would open an empty gallery — a dead end. Defaults to
	 * `true` so the common (square/two-room) path is unchanged.
	 */
	hasTemplates?: boolean;
	/** Fired once when the intro is dismissed (skip, finish, or a choice). */
	onDismiss: () => void;
	/** CSS selector for the canvas coach-mark anchor (viewer area). */
	canvasSelector?: string;
	/** CSS selector for the workspace-tabs coach-mark anchor. */
	tabsSelector?: string;
}

interface Spotlight {
	top: number;
	left: number;
	width: number;
	height: number;
}

/** Measure a target element; null when it's absent or unmeasurable (jsdom). */
function measureSpotlight(selector: string): Spotlight | null {
	if (typeof document === "undefined") return null;
	const el = document.querySelector(selector);
	if (!el) return null;
	const rect = el.getBoundingClientRect();
	if (rect.width === 0 && rect.height === 0) return null;
	return {
		top: rect.top,
		left: rect.left,
		width: rect.width,
		height: rect.height,
	};
}

/**
 * First-run onboarding: a skippable 3-beat intro — template-first entry,
 * then two coach-marks (3D interaction, workspace tabs). Persistence and
 * the show-once/skip-on-share gate live in builderIntroState.ts.
 */
export function BuilderIntro({
	open,
	onChooseTemplate,
	onChooseBlank,
	onDismiss,
	hasTemplates = true,
	canvasSelector = ".builder-content",
	tabsSelector = ".builder-workspace-nav",
}: BuilderIntroProps) {
	const t = useT();
	const titleId = useId();
	const bodyId = useId();
	const [step, setStep] = useState<BuilderIntroStep>("entry");
	const [spotlight, setSpotlight] = useState<Spotlight | null>(null);

	const dialogRef = useRef<HTMLDivElement | null>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const dismissedRef = useRef(false);

	// Suppress the viewer-help auto-open for the lifetime of the intro so
	// two overlays never auto-open at once. Runs once on mount.
	useEffect(() => {
		if (!open) return;
		suppressViewerHelpAutoOpen();
		previousFocusRef.current =
			document.activeElement instanceof HTMLElement
				? document.activeElement
				: null;
	}, [open]);

	// Single dismissal path: mark seen, restore focus, notify parent once.
	const dismiss = useCallback(() => {
		if (dismissedRef.current) return;
		dismissedRef.current = true;
		markIntroSeen();
		previousFocusRef.current?.focus();
		previousFocusRef.current = null;
		onDismiss();
	}, [onDismiss]);

	const handleChooseTemplate = useCallback(() => {
		onChooseTemplate();
		dismiss();
	}, [onChooseTemplate, dismiss]);

	const handleChooseBlank = useCallback(() => {
		onChooseBlank();
		// Move on to the coach-marks instead of closing outright: "start
		// blank" keeps the user in the builder, so orient them.
		setStep("canvas");
	}, [onChooseBlank]);

	const handleAdvance = useCallback(() => {
		setStep((current) => nextIntroStep(current) ?? current);
		const next = nextIntroStep(step);
		if (next === null) dismiss();
	}, [step, dismiss]);

	// Position the coach-mark spotlight over the real anchor element.
	// Re-measure on resize/scroll; degrade to a centered callout in jsdom
	// or when the anchor can't be measured.
	useEffect(() => {
		if (!open || step === "entry") {
			setSpotlight(null);
			return;
		}
		const selector = step === "canvas" ? canvasSelector : tabsSelector;
		const remeasure = () => setSpotlight(measureSpotlight(selector));
		remeasure();
		window.addEventListener("resize", remeasure);
		window.addEventListener("scroll", remeasure, true);
		return () => {
			window.removeEventListener("resize", remeasure);
			window.removeEventListener("scroll", remeasure, true);
		};
	}, [open, step, canvasSelector, tabsSelector]);

	// Escape always closes; Tab is trapped inside the entry dialog.
	useEffect(() => {
		if (!open) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				dismiss();
				return;
			}
			if (event.key !== "Tab" || step !== "entry") return;
			const dialog = dialogRef.current;
			if (!dialog) return;
			const focusable = Array.from(
				dialog.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
				),
			);
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open, step, dismiss]);

	// Focus the entry dialog when it mounts so the trap has an anchor.
	useEffect(() => {
		if (open && step === "entry") dialogRef.current?.focus();
	}, [open, step]);

	if (!open) return null;

	if (step === "entry") {
		return (
			<div className="builder-intro__backdrop">
				<div
					ref={dialogRef}
					className="builder-intro__dialog"
					role="dialog"
					aria-modal="true"
					aria-labelledby={titleId}
					aria-describedby={bodyId}
					tabIndex={-1}
				>
					<h2 id={titleId} className="builder-intro__title">
						{t.builderIntro_entryTitle}
					</h2>
					<p id={bodyId} className="builder-intro__subtitle">
						{t.builderIntro_entrySubtitle}
					</p>
					<div className="builder-intro__choices">
						{hasTemplates && (
							<button
								type="button"
								className="builder-intro__choice builder-intro__choice--primary"
								onClick={handleChooseTemplate}
							>
								<span className="builder-intro__choice-title">
									{t.builderIntro_entryTemplate}
								</span>
								<span className="builder-intro__choice-hint">
									{t.builderIntro_entryTemplateHint}
								</span>
							</button>
						)}
						<button
							type="button"
							className={
								hasTemplates
									? "builder-intro__choice"
									: "builder-intro__choice builder-intro__choice--primary"
							}
							onClick={handleChooseBlank}
						>
							<span className="builder-intro__choice-title">
								{hasTemplates
									? t.builderIntro_entryBlank
									: t.builderIntro_entrySoloStart}
							</span>
							<span className="builder-intro__choice-hint">
								{t.builderIntro_entryBlankHint}
							</span>
						</button>
					</div>
					<button
						type="button"
						className="builder-intro__skip"
						onClick={dismiss}
					>
						{t.builderIntro_skip}
					</button>
				</div>
			</div>
		);
	}

	// Coach-mark steps (canvas, tabs).
	const { current, total } = introStepPosition(step);
	const stepLabel = t.builderIntro_stepLabel
		.replace("{current}", String(current))
		.replace("{total}", String(total));
	const isLast = nextIntroStep(step) === null;
	const coachTitle =
		step === "canvas" ? t.builderIntro_canvasTitle : t.builderIntro_tabsTitle;
	const coachBody =
		step === "canvas" ? t.builderIntro_canvasBody : t.builderIntro_tabsBody;

	return (
		<div className="builder-intro__coach-layer">
			{spotlight && (
				<div
					className="builder-intro__spotlight"
					style={{
						top: `${spotlight.top}px`,
						left: `${spotlight.left}px`,
						width: `${spotlight.width}px`,
						height: `${spotlight.height}px`,
					}}
					aria-hidden="true"
				/>
			)}
			<div
				className={`builder-intro__coach builder-intro__coach--${step}${
					spotlight ? "" : " builder-intro__coach--centered"
				}`}
				role="dialog"
				aria-live="polite"
				aria-labelledby={titleId}
				aria-describedby={bodyId}
			>
				<span className="builder-intro__step-label">{stepLabel}</span>
				<h2 id={titleId} className="builder-intro__coach-title">
					{coachTitle}
				</h2>
				<p id={bodyId} className="builder-intro__coach-body">
					{coachBody}
				</p>
				<div className="builder-intro__coach-actions">
					<button
						type="button"
						className="builder-intro__skip"
						onClick={dismiss}
					>
						{t.builderIntro_skip}
					</button>
					<button
						type="button"
						className="builder-intro__next"
						onClick={handleAdvance}
					>
						{isLast ? t.builderIntro_done : t.builderIntro_next}
					</button>
				</div>
			</div>
		</div>
	);
}
